import { useState } from "react";

import {
  Users,
  Rocket,
  MoreVertical,
  Link2,
  Copy,
  PencilLine,
  CirclePlus,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { expDarkIcon, rotation3dWhiteIcon } from "@/lib/Images";
import { InfoBadge, StatusBadge } from "@/components/common/ProfileBadges";
import { formatDate } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

const CampaignCard = ({
  campaign,
  onCopy,
  onView,
  onAddExperience,
  onEditCampaign,
  deepLink,
  onRemoveCampaign,
}) => {
  const [copiedType, setCopiedType] = useState("");

  const showCopiedTooltip = (type) => {
    setCopiedType(type);

    setTimeout(() => {
      setCopiedType("");
    }, 2000);
  };


  const handleCopyLink = async () => {
    // const inviteLink = `${window.location.origin}/invite/${campaign.id}`;
     const inviteLink = `${deepLink}`;

    const copied = await onCopy(inviteLink, "Invite link copied successfully!");

    if (copied) {
      showCopiedTooltip("link");
    }
  };
  const handleCopyCode = () => {
    onCopy(campaign.joinCode);
    showCopiedTooltip("code");
  };

  return (
    <div className="rounded-lg border border-brand-17 bg-brand-100 px-5 py-5 shadow-lg">
      <div className="mb-5 flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#08334B] to-[#2F1852]">
            <Rocket className="h-8 w-8 text-[#9C6BFF]" />
          </div>

          <div>
            <h3 className="text-2xl font-bold break-words">{campaign.name}</h3>
            <p className="mt-1 text-sm text-neutral-100/60">
              Created {formatDate(campaign.createdAt)}
            </p>
          </div>
        </div>

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-11 text-neutral-100 hover:bg-neutral-100/5"
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="w-56 border border-brand-11 bg-brand-100 p-0 text-neutral-100"
          >
            <DropdownMenuItem
              onClick={onView}
              className="cursor-pointer px-4 py-3 text-sm font-semibold text-neutral-100 focus:bg-[#121A45] focus:text-neutral-100"
            >
              <img src={rotation3dWhiteIcon} className="h-5 w-5" />
              View all experiences
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={onAddExperience}
              className="cursor-pointer px-4 py-3 text-sm font-semibold text-neutral-100 focus:bg-[#121A45] focus:text-neutral-100"
            >
              <CirclePlus className="h-5 w-5 text-neutral-100" />
              Add a new experience
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={onEditCampaign}
              className="cursor-pointer px-4 py-3 text-sm font-semibold text-neutral-100 focus:bg-[#121A45] focus:text-neutral-100"
            >
              <PencilLine className="h-5 w-5" />
              Edit campaign details
            </DropdownMenuItem>

            {/* <DropdownMenuItem
              onClick={onRemoveCampaign}
              className="cursor-pointer px-4 py-3 text-sm font-semibold text-[#FF4D4D] focus:bg-[#2A1010] focus:text-[#FF4D4D]"
            >
              <Trash2 className="h-5 w-5" />
              Remove campaign
            </DropdownMenuItem> */}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <p className="mb-7 text-sm leading-relaxed break-words text-neutral-100/70">
        {campaign.description}
      </p>

      <div className="mb-7 flex flex-wrap items-center gap-3">
        <StatusBadge className="bg-[#064D23] text-[#00E261]">
          {campaign.status}
        </StatusBadge>

        <InfoBadge icon={<Users className="h-4 w-4 text-[#00D3F2]" />}>
          {campaign.noOfUsers} Users
        </InfoBadge>

        <InfoBadge icon={<img src={expDarkIcon} className="h-4 w-4" />}>
          {campaign.noOfExperiences} Experiences
        </InfoBadge>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm font-bold text-neutral-100">
          Access:{" "}
          {campaign.access === "INVITE_ONLY" ? "Invite Only" : campaign.access}
        </p>

        <div className="flex flex-wrap gap-3">
          <div className="relative">
            {/* <Button
              type="button"
              variant="outline"
              // onClick={handleCopyLink}
              onClick={() => handleCopyLink(campaign._id)}
              className="h-9 border-brand-11 bg-transparent px-4 text-sm font-bold text-neutral-100 hover:bg-neutral-100/5 hover:text-neutral-100"
            >
              Copy Link
              <Link2 className="ml-2 h-4 w-4" />
            </Button> */}

            {copiedType === "link" && (
              <div className="absolute -top-9 left-1/2 z-20 -translate-x-1/2 rounded-md bg-brand-100 px-3 py-1 text-xs font-semibold text-neutral-100 shadow-lg">
                Copied
              </div>
            )}
          </div>

          {/* <div className="relative">
            <Button
              type="button"
              variant="outline"
              onClick={handleCopyCode}
              className="h-9 border-brand-15 bg-transparent px-4 text-sm font-bold text-neutral-100 hover:bg-neutral-100/5 hover:text-neutral-100"
            >
              Copy Code
              <Copy className="ml-2 h-4 w-4" />
            </Button>

            {copiedType === "code" && (
              <div className="absolute -top-9 left-1/2 z-20 -translate-x-1/2 rounded-md bg-brand-100 px-3 py-1 text-xs font-semibold text-neutral-100 shadow-lg">
                Copied
              </div>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default CampaignCard;
